import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { compareVersions } from "./cveVersionUtil";

// Le cache stocke `severity` en chaîne ("6.1"), souvent null. Normalisé une fois ici pour
// que les comparaisons en aval ne reposent pas sur la coercion JS.
function normalizeVendors(vendors) {
  const normalized = {};
  Object.keys(vendors || {}).forEach((vendor) => {
    normalized[vendor] = (vendors[vendor] || []).map((cve) => {
      const raw = cve?.severity;
      const score = raw == null || raw === "" ? null : Number(raw);
      return {
        ...cve,
        severity: score != null && Number.isFinite(score) ? score : null,
      };
    });
  });
  return normalized;
}

// Hook pour récupérer les CVE en cache
export function useCveCache() {
  const [data, setData] = useState({ meta: {}, vendors: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCache = async () => {
      setIsLoading(true);
      try {
        const token = sessionStorage.getItem("token");
        const resp = await axios.get(
          process.env.REACT_APP_DOMAIN_URL + "/api/cves_cached",
          {
            timeout: 20000,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }
        );
        if (!cancelled) {
          const payload = resp.data || {};
          setData({
            meta: payload.meta || {},
            vendors: normalizeVendors(payload.vendors),
          });
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || String(err));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchCache();
    return () => { cancelled = true; };
  }, []);

  return { data, isLoading, error };
}

// Hook pour filtrer les plugins selon les CVEs
export function useFilteredForTable(filteredPlugins) {
  const { data: cveCache, isLoading: cveLoading, error: cveError } = useCveCache();

  // Liste unique des vendors présents dans les plugins filtrés
  const vendors = useMemo(() => {
    return Array.from(
      new Set(
        filteredPlugins.flatMap(item =>
          (item.plugins || []).map(p => p.slug?.trim().toLowerCase()).filter(Boolean)
        )
      )
    );
  }, [filteredPlugins]);

  // Mapping vendor => CVEs
  const cvesByVendor = useMemo(() => {
    if (!cveCache?.vendors) return {};
    const mapping = {};
    vendors.forEach(vendor => {
      mapping[vendor] = cveCache.vendors[vendor] || [];
    });
    return mapping;
  }, [cveCache, vendors]);

  // Filtrage principal pour la table
  const filteredForTable = useMemo(() => {
    if (!Array.isArray(filteredPlugins)) return [];

    // On ne conserve que les plugins directement affectés par des CVE
    return filteredPlugins
      .map((group) => {
        const pluginsFiltered = (group.plugins || [])
          .map((plugin) => {
            const vendorKey = plugin.slug?.trim().toLowerCase();
            const vendorCves = cvesByVendor[vendorKey] || [];
            if (!vendorCves.length) return null;

            // On ne garde que les CVE qui affectent les plugins dans les versions utilisées
            const impactedCves = vendorCves.filter((cve) => {
              const cveMax = cve.maxversion;
              if (!cveMax) return false;
              const cveMaxNormalized = String(cveMax).trim().replace(/^v/i, "");
              return (plugin.sites || []).some((site) => {
                const version = site?.versionplugins;
                if (!version) return false;
                return compareVersions(version, cveMaxNormalized) === -1;
              });
            });

            if (!impactedCves.length) return null;

            // Ne dépend pas du site : calculée une fois par plugin, et non une fois par
            // site comme dans le filter ci-dessous.
            const maxImpactedVersion = impactedCves
              .map((c) => c.maxversion)
              .filter(Boolean)
              .reduce(
                (max, cur) => (max == null ? cur : (compareVersions(cur, max) === 1 ? cur : max)),
                null
              );

            if (!maxImpactedVersion) return null;
            const maxImpactedNormalized = String(maxImpactedVersion).trim().replace(/^v/i, "");

            // On ne garde que les sites impactés
            const sitesFiltered = (plugin.sites || []).filter((site) => {
              const version = site?.versionplugins;
              if (!version) return false;
              return compareVersions(version, maxImpactedNormalized) === -1;
            });

            if (!sitesFiltered.length) return null;

            // On construit un tableau unique de sites
            const sitesArray = [
              ...new Set(sitesFiltered.map((s) => s.siteUrl || s.name || s.host).filter(Boolean))
            ];

            // On récupère la plus grosse sévérité parmi les CVE du plugin
            const severity = impactedCves.reduce((max, cve) => {
              const score = cve?.severity ?? null;
              if (score == null) return max;
              return Math.max(max ?? 0, score);
            }, null);

            return {
              ...plugin,
              sites: sitesFiltered,
              sitesArray,
              occurence: sitesArray.length,
              // Liste complète du vendor d'un côté, CVE réellement impactantes de l'autre.
              cves: vendorCves,
              impactedCves,
              severity,
              maxImpactedVersion: maxImpactedNormalized,
            };
          })
          .filter(Boolean);

        if (!pluginsFiltered.length) return null;

        return {
          ...group,
          plugins: pluginsFiltered,
        };
      })
      .filter(Boolean);
    // cvesByVendor dérive déjà de cveCache : l'ajouter aux deps relancerait ce calcul
    // pour rien.
  }, [filteredPlugins, cvesByVendor]);

  return { filteredForTable, cveLoading, cveError };
}
